import React, { useState, useMemo } from "react";
import { List, Avatar, Badge, Spin, Empty, Typography, Tag, Tabs, Collapse } from "antd";
import {
  UserOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  AimOutlined,
  SmileOutlined,
  PlusOutlined,
  SendOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ButtonItem } from "../../../../../Components/Items";
import { WA } from "../../theme";
import { formatTelefono, inicialesDe, colorAvatar } from "../../../../../Utils/whatsapp.utils";

const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const TABS_CSS = `
  .wa-tabs .ant-tabs-nav { margin: 0 !important; padding: 0 12px !important; }
  .wa-tabs .ant-tabs-nav::before { border-bottom: 1px solid ${WA.border} !important; }
  .wa-tabs .ant-tabs-tab {
    padding: 8px 0 !important;
    margin: 0 18px 0 0 !important;
    font-size: 12px !important;
  }
  .wa-tabs .ant-tabs-tab .ant-tabs-tab-btn { color: ${WA.textSecondary} !important; }
  .wa-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn { color: ${WA.limaFuerte} !important; }
  .wa-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${WA.textPrimary} !important;
    font-weight: 600;
  }
  .wa-tabs .ant-tabs-ink-bar { background: ${WA.limaFuerte} !important; height: 2px !important; }
  .wa-tabs .ant-tabs-content-holder { background: transparent !important; }
`;

const PLACEHOLDERS = {
  "[imagen]": { icon: <PictureOutlined />, label: "Foto" },
  "[video]": { icon: <VideoCameraOutlined />, label: "Video" },
  "[audio]": { icon: <AudioOutlined />, label: "Audio" },
  "[documento]": { icon: <FileTextOutlined />, label: "Documento" },
  "[ubicación]": { icon: <EnvironmentOutlined />, label: "Ubicación" },
  "[sticker]": { icon: <SmileOutlined />, label: "Sticker" },
  "[solicitud de ubicación]": { icon: <AimOutlined />, label: "Solicitud de ubicación" },
};

const PreviewUltimo = ({ texto }) => {
  const ph = PLACEHOLDERS[texto];
  if (ph) {
    return (
      <Text ellipsis style={{ fontSize: 12, maxWidth: 230, color: WA.textSecondary, display: "block" }}>
        <span style={{ marginRight: 5 }}>{ph.icon}</span>
        {ph.label}
      </Text>
    );
  }
  return (
    <Text ellipsis style={{ fontSize: 12, maxWidth: 230, color: WA.textSecondary, display: "block" }}>
      {texto || ""}
    </Text>
  );
};

const nombreDe = (conv) => {
  if (conv.contactoId?.nombre) {
    return `${conv.contactoId.nombre} ${conv.contactoId.apellidoPaterno || ""}`.trim();
  }
  return conv.nombrePerfilWa || formatTelefono(conv.telefono);
};

const nombreCompleto = (u) =>
  u ? [u.nombre, u.apellidoPaterno, u.apellidoMaterno].filter(Boolean).join(" ") : null;

const idLinea = (conv) => {
  const l = conv.lineaWhatsappId;
  if (!l) return null;
  return String(l._id || l);
};

const ConversacionItem = ({ conv, activa, onSelect, mostrarAsignado }) => {
  const [hover, setHover] = useState(false);
  const fondo = activa ? WA.limaTintFuerte : hover ? WA.limaTint : "transparent";
  const nombre = nombreDe(conv);
  const iniciales = inicialesDe(nombre);
  const pastel = colorAvatar(conv.telefono);

  return (
    <List.Item
      onClick={() => onSelect(conv)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        padding: "10px 14px",
        backgroundColor: fondo,
        borderLeft: `3px solid ${activa ? WA.limaFuerte : "transparent"}`,
        transition: "background-color 0.2s ease",
      }}
    >
      <List.Item.Meta
        avatar={
          <Badge count={conv.noLeidos || 0} size="small" style={{ backgroundColor: WA.naranja }}>
            <Avatar style={{ backgroundColor: pastel.fondo, color: pastel.texto, fontWeight: 600 }}>
              {iniciales || <UserOutlined />}
            </Avatar>
          </Badge>
        }
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong ellipsis style={{ maxWidth: 160, color: WA.textPrimary }}>
              {nombre}
            </Text>
            {conv.ultimoMensajeFecha && (
              <Text style={{ fontSize: 11, color: WA.textSecondary }}>
                {moment(conv.ultimoMensajeFecha).format("HH:mm")}
              </Text>
            )}
          </div>
        }
        description={
          <>
            <PreviewUltimo texto={conv.ultimoMensajeTexto} />
            {mostrarAsignado && (
              <Tag
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  lineHeight: "16px",
                  border: "none",
                  backgroundColor: conv.asignadoAUsuarioId ? WA.limaTint : "#f0f0f0",
                  color: WA.textSecondary,
                }}
              >
                {nombreCompleto(conv.asignadoAUsuarioId) || "Sin asignar"}
              </Tag>
            )}
          </>
        }
      />
    </List.Item>
  );
};

const Acciones = ({ onNuevaConversacion, onMensajeMasivo, permisoMensajeMasivo }) => (
  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
    {onNuevaConversacion && (
      <ButtonItem
        lima
        lessTime
        icon={<PlusOutlined />}
        tooltip="Nueva conversación"
        onClick={onNuevaConversacion}
      />
    )}
    {permisoMensajeMasivo && onMensajeMasivo && (
      <ButtonItem
        naranja
        lessTime
        icon={<SendOutlined />}
        tooltip="Mensaje masivo"
        onClick={onMensajeMasivo}
      />
    )}
  </div>
);

const HeaderAcciones = ({ onNuevaConversacion, onMensajeMasivo, permisoMensajeMasivo }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "6px 12px",
      borderBottom: `1px solid ${WA.border}`,
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 12, color: WA.textSecondary, fontWeight: 500 }}>
      Conversaciones
    </span>
    <Acciones
      onNuevaConversacion={onNuevaConversacion}
      onMensajeMasivo={onMensajeMasivo}
      permisoMensajeMasivo={permisoMensajeMasivo}
    />
  </div>
);

const AvisoNumeroEnvio = ({ numero, ajeno, duenoAjeno, acciones }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "8px 12px",
      borderBottom: `1px solid ${WA.border}`,
      backgroundColor: ajeno ? "#fff7e6" : WA.limaTint,
      flexShrink: 0,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, color: WA.textSecondary, textTransform: "uppercase", letterSpacing: 0.4 }}>
        Enviando desde
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
        <PhoneOutlined style={{ color: ajeno ? WA.naranja : WA.limaFuerte }} />
        <Text strong style={{ color: WA.textPrimary, fontSize: 13 }}>
          {numero || "Sin número conectado"}
        </Text>
      </div>
      {ajeno && (
        <div style={{ fontSize: 11, color: WA.naranja, marginTop: 2 }}>
          Es el número de {duenoAjeno || "otro usuario"}, no el tuyo.
        </div>
      )}
    </div>
    {acciones}
  </div>
);

const ListaPlana = ({ conversaciones, conversacionActivaId, onSelect, mostrarAsignado, vacio }) => {
  if (!conversaciones.length) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description={vacio || "Sin conversaciones"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }
  return (
    <List
      dataSource={conversaciones}
      renderItem={(conv) => (
        <ConversacionItem
          conv={conv}
          activa={conv._id === conversacionActivaId}
          onSelect={onSelect}
          mostrarAsignado={mostrarAsignado}
        />
      )}
    />
  );
};

const CabeceraUsuario = ({ grupo }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
    <Avatar size="small" style={{ backgroundColor: WA.limaFuerte, color: "#fff", fontWeight: 600 }}>
      {inicialesDe(grupo.nombre) || <UserOutlined />}
    </Avatar>
    <div style={{ minWidth: 0, flex: 1 }}>
      <Text strong ellipsis style={{ display: "block", color: WA.textPrimary, fontSize: 13 }}>
        {grupo.nombre}
      </Text>
      <Text style={{ fontSize: 11, color: WA.textSecondary }}>
        {grupo.numeroDisplay || "Sin número"} · {grupo.conversaciones.length}
      </Text>
    </div>
    {grupo.noLeidos > 0 && (
      <Badge count={grupo.noLeidos} size="small" style={{ backgroundColor: WA.naranja }} />
    )}
  </div>
);

const ChatsDeOtros = ({ grupos, conversacionActivaId, onSelect }) => {
  if (!grupos.length) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="Nadie más tiene chats" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }
  return (
    <Collapse ghost expandIconPosition="right" style={{ backgroundColor: "transparent" }}>
      {grupos.map((grupo) => (
        <Panel
          key={grupo.key}
          header={<CabeceraUsuario grupo={grupo} />}
          style={{ borderBottom: `1px solid ${WA.border}` }}
        >
          <ListaPlana
            conversaciones={grupo.conversaciones}
            conversacionActivaId={conversacionActivaId}
            onSelect={onSelect}
          />
        </Panel>
      ))}
    </Collapse>
  );
};

const ListaConversaciones = ({
  conversaciones,
  loading,
  conversacionActivaId,
  onSelect,
  esMaster,
  onNuevaConversacion,
  onMensajeMasivo,
  permisoMensajeMasivo,
  numeroPorUsuario,
  puedeDerivar,
  miLineaId,
  miNumero,
  lineaPrincipalId,
  numeroEnvioActivo,
  duenoNumeroActivo,
}) => {
  const showHeader = !!(onNuevaConversacion || (permisoMensajeMasivo && onMensajeMasivo));

  const buzonAparte =
    !!lineaPrincipalId && String(lineaPrincipalId) !== String(miLineaId || "");

  const { mias, buzon, grupos } = useMemo(() => {
    if (!numeroPorUsuario) return { mias: [], buzon: [], grupos: [] };

    const mias = [];
    const buzon = [];
    const porUsuario = new Map();

    conversaciones.forEach((conv) => {
      const lineaId = idLinea(conv);
      if (miLineaId && lineaId === String(miLineaId)) {
        mias.push(conv);
        return;
      }
      if (buzonAparte && lineaId === String(lineaPrincipalId)) {
        buzon.push(conv);
        return;
      }
      const linea = conv.lineaWhatsappId;
      const dueno = linea?.usuarioId;
      const key = dueno?._id ? String(dueno._id) : `sin-linea-${lineaId || "0"}`;
      if (!porUsuario.has(key)) {
        porUsuario.set(key, {
          key,
          nombre: nombreCompleto(dueno) || "Sin número asignado",
          numeroDisplay: linea?.numeroDisplay || null,
          conversaciones: [],
          noLeidos: 0,
        });
      }
      const grupo = porUsuario.get(key);
      grupo.conversaciones.push(conv);
      grupo.noLeidos += conv.noLeidos || 0;
    });

    return { mias, buzon, grupos: Array.from(porUsuario.values()) };
  }, [conversaciones, numeroPorUsuario, miLineaId, lineaPrincipalId, buzonAparte]);

  const cabecera = (
    <>
      {showHeader && (
        <HeaderAcciones
          onNuevaConversacion={onNuevaConversacion}
          onMensajeMasivo={onMensajeMasivo}
          permisoMensajeMasivo={permisoMensajeMasivo}
        />
      )}
      {numeroPorUsuario && (numeroEnvioActivo || miNumero) && (
        <AvisoNumeroEnvio
          numero={numeroEnvioActivo || miNumero}
          ajeno={!!numeroEnvioActivo && numeroEnvioActivo !== miNumero}
          duenoAjeno={duenoNumeroActivo}
        />
      )}
    </>
  );

  if (loading) {
    return (
      <>
        {cabecera}
        <div style={{ textAlign: "center", padding: 30 }}>
          <Spin />
        </div>
      </>
    );
  }

  if (!numeroPorUsuario) {
    return (
      <>
        {cabecera}
        <ListaPlana
          conversaciones={conversaciones}
          conversacionActivaId={conversacionActivaId}
          onSelect={onSelect}
          mostrarAsignado={esMaster}
        />
      </>
    );
  }

  const panes = [];
  if (miLineaId) {
    panes.push(
      <TabPane tab="Conversaciones" key="mias">
        <ListaPlana
          conversaciones={mias}
          conversacionActivaId={conversacionActivaId}
          onSelect={onSelect}
          vacio="Todavía no te escriben a tu número"
        />
      </TabPane>,
    );
  }
  if (puedeDerivar && buzonAparte) {
    panes.push(
      <TabPane tab="Buzón" key="buzon">
        <ListaPlana
          conversaciones={buzon}
          conversacionActivaId={conversacionActivaId}
          onSelect={onSelect}
          vacio="El número principal no tiene chats por repartir"
        />
      </TabPane>,
    );
  }
  if (puedeDerivar) {
    panes.push(
      <TabPane tab="Otros" key="otros">
        <ChatsDeOtros
          grupos={grupos}
          conversacionActivaId={conversacionActivaId}
          onSelect={onSelect}
        />
      </TabPane>,
    );
  }

  if (panes.length <= 1) {
    return (
      <>
        {cabecera}
        <ListaPlana
          conversaciones={miLineaId ? mias : conversaciones}
          conversacionActivaId={conversacionActivaId}
          onSelect={onSelect}
          vacio="Todavía no te escriben a tu número"
        />
      </>
    );
  }

  return (
    <>
      <style>{TABS_CSS}</style>
      <AvisoNumeroEnvio
        numero={numeroEnvioActivo || miNumero}
        ajeno={!!numeroEnvioActivo && numeroEnvioActivo !== miNumero}
        duenoAjeno={duenoNumeroActivo}
        acciones={
          showHeader ? (
            <Acciones
              onNuevaConversacion={onNuevaConversacion}
              onMensajeMasivo={onMensajeMasivo}
              permisoMensajeMasivo={permisoMensajeMasivo}
            />
          ) : null
        }
      />
      <Tabs className="wa-tabs" size="small" destroyInactiveTabPane style={{ flex: 1, minHeight: 0 }}>
        {panes}
      </Tabs>
    </>
  );
};

export default ListaConversaciones;
